import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";
import { BookButton } from "@/components/BookButton";
import { NAV_LINKS } from "@/lib/site";
import { cn } from "@/lib/utils";

export function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-nav"
            className={cn(
                "fixed top-0 inset-x-0 z-50 transition-colors duration-500",
                scrolled ? "bg-background/85 backdrop-blur-md border-b border-border" : "bg-transparent border-b border-transparent"
            )}
        >
            <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
                <Wordmark />

                <div className="hidden md:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-${l.href.slice(1)}`}
                            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                    <BookButton className="px-5 py-2 text-sm" testid="nav-book-a-call" />
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setMenuOpen((o) => !o)}
                    className="md:hidden h-10 w-10 grid place-items-center rounded-full border border-border"
                    aria-label="Menu"
                >
                    {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </nav>

            <AnimatePresence>
                {menuOpen && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="md:hidden overflow-hidden bg-background border-b border-border"
                    >
                        <div className="px-5 py-6 flex flex-col gap-5">
                            {NAV_LINKS.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    onClick={() => setMenuOpen(false)}
                                    className="text-base text-foreground"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <BookButton className="w-full justify-center" testid="mobile-book-a-call" />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
